import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { ADMIN_PERMISSION_ACTIONS, ADMIN_PERMISSION_MODULES, hasAdminPermission } from "../utils/adminPermissions";
import { sweetConfirm } from "../utils/sweetAlerts";

const API = "/backend/public/api";

const blankPermissions = () =>
  ADMIN_PERMISSION_MODULES.reduce((acc, module) => {
    acc[module.key] = { view: false, edit: false, delete: false };
    return acc;
  }, {});

const emptyForm = {
  name: "",
  email: "",
  admin_type: "Admin",
  password: "",
  is_active: true,
  permissions: blankPermissions(),
};

const panelStyle = {
  background: "#ffffff",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: "18px",
  padding: "22px",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};

function AdminUserController() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const canEdit = hasAdminPermission("settings", "edit");
  const canDelete = hasAdminPermission("settings", "delete");
  const isEditing = Boolean(editingId);

  const selectedCount = useMemo(
    () =>
      Object.values(form.permissions).reduce(
        (total, modulePermissions) =>
          total + ADMIN_PERMISSION_ACTIONS.filter((action) => modulePermissions?.[action]).length,
        0
      ),
    [form.permissions]
  );

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/users`);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setMessage(error.response?.data?.message || "Admin user list load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const togglePermission = (moduleKey, action) => {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: {
          ...prev.permissions[moduleKey],
          [action]: !prev.permissions[moduleKey]?.[action],
        },
      },
    }));
  };

  const setModuleAll = (moduleKey, checked) => {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: { view: checked, edit: checked, delete: checked },
      },
    }));
  };

  const setAllPermissions = (checked) => {
    setForm((prev) => ({
      ...prev,
      permissions: ADMIN_PERMISSION_MODULES.reduce((acc, module) => {
        acc[module.key] = { view: checked, edit: checked, delete: checked };
        return acc;
      }, {}),
    }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!canEdit) {
      setMessage("Ei action-er edit permission apnar account-e nei");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      const payload = { ...form };
      if (isEditing && !payload.password) {
        delete payload.password;
      }

      const res = isEditing
        ? await axios.put(`${API}/admin/users/${editingId}`, payload)
        : await axios.post(`${API}/admin/users`, payload);

      setMessage(res.data?.message || "Admin user saved successfully");
      resetForm();
      await loadUsers();
    } catch (error) {
      setMessage(error.response?.data?.message || "Admin user save failed");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setForm({
      name: user.name || "",
      email: user.email || "",
      admin_type: user.admin_type || "Admin",
      password: "",
      is_active: Boolean(user.is_active),
      permissions: { ...blankPermissions(), ...(user.permissions || {}) },
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteUser = async (id) => {
    if (!canDelete) {
      setMessage("Ei action-er delete permission apnar account-e nei");
      return;
    }
    if (!(await sweetConfirm("Delete this admin user?", { confirmButtonText: "Yes, Delete" }))) return;

    try {
      setSaving(true);
      const res = await axios.delete(`${API}/admin/users/${id}`);
      setMessage(res.data?.message || "Admin user deleted successfully");
      await loadUsers();
    } catch (error) {
      setMessage(error.response?.data?.message || "Admin user delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ display: "grid", gap: "20px" }}>
        <section
          style={{
            background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)",
            borderRadius: "24px",
            padding: "26px",
            color: "#ffffff",
            boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)",
          }}
        >
          <p style={{ margin: 0, fontSize: "12px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.84 }}>
            Settings / User Controller
          </p>
          <h1 style={{ margin: "10px 0 8px", fontSize: "32px", lineHeight: 1.15 }}>Create admin users with part-wise website permission</h1>
          <p style={{ margin: 0, maxWidth: "760px", color: "rgba(255,255,255,0.84)", lineHeight: 1.7 }}>
            View, Edit, Delete permission module-wise set korun. User save hole niche list-e edit, delete, change permission action paben.
          </p>
        </section>

        <form onSubmit={submitForm} style={{ ...panelStyle, display: "grid", gap: "18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <h3 style={{ margin: 0, color: "#0f172a" }}>{isEditing ? "Edit Admin User" : "New Admin User"}</h3>
              <p style={{ margin: "6px 0 0", color: "#64748b" }}>{selectedCount} permission selected</p>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setAllPermissions(true)} disabled={!canEdit}>
                Select All
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAllPermissions(false)} disabled={!canEdit}>
                Clear All
              </button>
              {isEditing ? (
                <button type="button" className="btn btn-outline-dark btn-sm" onClick={resetForm}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
            <div>
              <label className="form-label fw-semibold">Full Name</label>
              <input className="form-control" value={form.name} onChange={(event) => updateField("name", event.target.value)} required disabled={!canEdit} />
            </div>
            <div>
              <label className="form-label fw-semibold">Email / Login ID</label>
              <input type="email" className="form-control" value={form.email} onChange={(event) => updateField("email", event.target.value)} required disabled={!canEdit} />
            </div>
            <div>
              <label className="form-label fw-semibold">Admin Type</label>
              <input className="form-control" value={form.admin_type} onChange={(event) => updateField("admin_type", event.target.value)} placeholder="Example: Accounts Admin" required disabled={!canEdit} />
            </div>
            <div>
              <label className="form-label fw-semibold">{isEditing ? "New Password" : "Password"}</label>
              <input type="password" className="form-control" value={form.password} onChange={(event) => updateField("password", event.target.value)} required={!isEditing} disabled={!canEdit} />
            </div>
          </div>

          <label style={{ display: "inline-flex", gap: "8px", alignItems: "center", fontWeight: 700, color: "#334155" }}>
            <input type="checkbox" checked={form.is_active} onChange={(event) => updateField("is_active", event.target.checked)} disabled={!canEdit} />
            Active user
          </label>

          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: "210px" }}>Website Part</th>
                  <th className="text-center">View</th>
                  <th className="text-center">Edit</th>
                  <th className="text-center">Delete</th>
                  <th className="text-center">All</th>
                </tr>
              </thead>
              <tbody>
                {ADMIN_PERMISSION_MODULES.map((module) => {
                  const modulePermissions = form.permissions[module.key] || {};
                  const allChecked = ADMIN_PERMISSION_ACTIONS.every((action) => modulePermissions[action]);
                  return (
                    <tr key={module.key}>
                      <td><strong>{module.label}</strong></td>
                      {ADMIN_PERMISSION_ACTIONS.map((action) => (
                        <td key={action} className="text-center">
                          <input
                            type="checkbox"
                            checked={Boolean(modulePermissions[action])}
                            onChange={() => togglePermission(module.key, action)}
                            disabled={!canEdit}
                          />
                        </td>
                      ))}
                      <td className="text-center">
                        <input type="checkbox" checked={allChecked} onChange={(event) => setModuleAll(module.key, event.target.checked)} disabled={!canEdit} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {message ? (
            <div style={{ color: message.toLowerCase().includes("failed") || message.toLowerCase().includes("nei") ? "#b91c1c" : "#166534", fontWeight: 700 }}>
              {message}
            </div>
          ) : null}

          <div>
            <button type="submit" className="btn btn-primary" disabled={saving || !canEdit}>
              {saving ? "Saving..." : isEditing ? "Update User" : "Create User"}
            </button>
          </div>
        </form>

        <section style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
            <div>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Admin User List</h3>
              <p style={{ margin: "6px 0 0", color: "#64748b" }}>Complete hoye jabar por created users ekhane show hobe.</p>
            </div>
            <button type="button" className="btn btn-outline-dark btn-sm" onClick={loadUsers} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sl</th>
                  <th>User</th>
                  <th>Admin Type</th>
                  <th>Status</th>
                  <th>Permission</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && users.map((user, index) => {
                  const count = Object.values(user.permissions || {}).reduce(
                    (total, modulePermissions) => total + ADMIN_PERMISSION_ACTIONS.filter((action) => modulePermissions?.[action]).length,
                    0
                  );

                  return (
                    <tr key={user.id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{user.name}</strong>
                        <div style={{ color: "#64748b", fontSize: "13px" }}>{user.email}</div>
                      </td>
                      <td>{user.admin_type}</td>
                      <td>
                        <span className={`badge ${user.is_active ? "text-bg-success" : "text-bg-secondary"}`}>
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{count} selected</td>
                      <td>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => startEdit(user)} disabled={!canEdit}>
                            Edit
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteUser(user.id)} disabled={!canDelete || saving}>
                            Delete
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => startEdit(user)} disabled={!canEdit}>
                            Change Permission
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!loading && users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">No admin user created yet.</td>
                  </tr>
                ) : null}
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">Loading admin users...</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export default AdminUserController;



